import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID || '';
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID || '';
const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN || '';

let _userPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!_userPool) {
    if (!userPoolId || !clientId) {
      throw new Error('Cognito configuration missing.');
    }
    _userPool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
  }
  return _userPool;
}

export interface AuthUser {
  email: string;
  sub: string;
  name?: string;
  picture?: string;
  attributes: Record<string, string>;
}

export const authService = {
  getCurrentUser(): CognitoUser | null {
    try {
      return getUserPool().getCurrentUser();
    } catch {
      return null;
    }
  },

  getSession(): Promise<CognitoUserSession | null> {
    return new Promise((resolve, reject) => {
      const user = this.getCurrentUser();
      if (!user) { resolve(null); return; }
      const timeout = setTimeout(() => resolve(null), 5000);
      user.getSession((err: Error | null, session: CognitoUserSession | null) => {
        clearTimeout(timeout);
        if (err) { reject(err); return; }
        resolve(session);
      });
    });
  },

  async getIdToken(): Promise<string | null> {
    const session = await this.getSession();
    return session?.getIdToken().getJwtToken() || null;
  },

  async getUserFromIdToken(): Promise<AuthUser | null> {
    const session = await this.getSession();
    if (!session) return null;
    const payload = session.getIdToken().decodePayload();
    return {
      email: payload.email || '',
      sub: payload.sub || '',
      name: payload.name,
      picture: payload.picture,
      attributes: payload as Record<string, string>,
    };
  },

  getCallbackUri(): string {
    return `${window.location.origin}/callback`;
  },

  signInWithGoogle(): void {
    const redirectUri = this.getCallbackUri();
    const scopes = 'email openid profile';
    const url = `${cognitoDomain}/oauth2/authorize?identity_provider=Google&client_id=${clientId}&response_type=code&scope=${encodeURIComponent(scopes)}&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = url;
  },

  async handleCallback(code: string): Promise<AuthUser> {
    const tokenEndpoint = `${cognitoDomain}/oauth2/token`;
    const redirectUri = this.getCallbackUri();

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code,
        redirect_uri: redirectUri,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange failed:', errorText);
      throw new Error('Failed to exchange code for tokens');
    }

    const tokens = await response.json();
    const idToken = tokens.id_token;
    const accessToken = tokens.access_token;
    const refreshToken = tokens.refresh_token;

    const idTokenPayload = JSON.parse(atob(idToken.split('.')[1]));
    const username = idTokenPayload['cognito:username'] || idTokenPayload.sub;

    const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
    localStorage.setItem(`${keyPrefix}.LastAuthUser`, username);
    localStorage.setItem(`${keyPrefix}.${username}.idToken`, idToken);
    localStorage.setItem(`${keyPrefix}.${username}.accessToken`, accessToken);
    if (refreshToken) {
      localStorage.setItem(`${keyPrefix}.${username}.refreshToken`, refreshToken);
    }
    localStorage.setItem(`${keyPrefix}.${username}.clockDrift`, '0');

    return {
      email: idTokenPayload.email || '',
      sub: idTokenPayload.sub || '',
      name: idTokenPayload.name,
      picture: idTokenPayload.picture,
      attributes: idTokenPayload as Record<string, string>,
    };
  },

  signOut(): void {
    const user = this.getCurrentUser();
    if (user) user.signOut();
  },
};
